import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { errorMsg } from "../../../Apis/functions";
import { TABLES } from "../../../Apis/Tables";
import { permissionObj } from "../../../Helpers/permission";
import { SEARCH_MODEL } from "../../../components/table/Model/TableModel";
import { DeleteItemsApi } from "../../../reduxToolkit/Commons/CommonsApi";
import { GetBuildingUserAssigned } from "../../../reduxToolkit/Building/BuildingApi";

/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 *
 * Assigned tab logic of the custom access (employees) section of the update
 * building modal. Mirrors AssignedShiftResourceApp: exposes the config, the
 * request body builder and the delete handlers consumed by AssignedTabResource.
 *
 * @param {Object} props
 * @param {number|string|null|undefined} props.id - Building id.
 * @param {Array<Object>} props.selected - Section selection list.
 * @param {Array} props.selectForDelete - Delete buffer (keyId values).
 * @param {Function} props.setSelectForDelete - Buffer setter.
 * @param {Function} props.setModified - Triggers a refetch of the assigned page.
 * @param {boolean} props.triggerDelete - External (header) bulk-delete trigger.
 * @param {Function} props.setTriggerDelete - Trigger setter.
 * @param {Function} props.setIsAllChecked - Select-all state setter.
 */

export const AssignedBuildingEmployeeApp = ({
                                                id,
                                                selected,
                                                selectForDelete,
                                                setSelectForDelete,
                                                setModified,
                                                triggerDelete,
                                                setTriggerDelete,
                                                setIsAllChecked,
                                            }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const deletePermission = DEV || permission?.includes(permissionObj?.WEB_BUILDING_MANAGE_USERS);

    const [deleteModalShow, setDeleteModalShow] = useState(false);

    const dynamicConfig = useMemo(() => ({
        keyId: 'userId',
        model: TABLES.BUILDING_USER,
        labelRowsPerPage: 'employees_per_page',
        tableNotInformationLegend: 'you_have_not_assigned_any_employee_yet',
        searchLabel: 'search_by_name_email_or_phone',
        title_modal: 'remove_employees',
        element_modal: 'employees',
    }), []);

    /**
     * Assigned request body. Uses the `searchElement` argument forwarded by
     * getData (not the closure) so reset/clear refetches with the right value;
     * an empty search is sent as `key: null`.
     */
    const buildBody = ({ page, rowsPerPage, searchElement }) => SEARCH_MODEL.setObject({
        page,
        size: rowsPerPage,
        key: searchElement,
        model: dynamicConfig.model,
        modelId: id ?? null
    });

    useEffect(() => {
        if (triggerDelete === true) {
            handleDelete();
            setTriggerDelete(false);
        }
    }, [triggerDelete]);

    /**
     * Opens the delete confirmation modal. When called with a row, that row is
     * the only item buffered for deletion; otherwise the current buffer is used.
     * @param {Object} [item] - Row to delete.
     */
    const handleDelete = (item) => {
        if (item) setSelectForDelete([item[dynamicConfig.keyId]]);
        setDeleteModalShow(true);
    };

    const handleCheckboxChange = (e) => {
        const key = e?.target?.[dynamicConfig.keyId];
        const checked = e?.target?.checked;
        setSelectForDelete(prev =>
            checked ? [...new Set([...prev, key])] : prev.filter(k => k !== key)
        );
    };

    /**
     * Toggles the "select all" checkbox: buffers every section item for
     * deletion when checking, clears the buffer when unchecking.
     */
    const handleDeleteAll = () => {
        setIsAllChecked(prev => {
            const next = !prev;
            setSelectForDelete(next ? selected.map(e => e[dynamicConfig.keyId]) : []);
            return next;
        });
    };

    /**
     * Closes the delete modal and clears the delete buffer / select-all state.
     */
    const hide = () => {
        setSelectForDelete([]);
        setIsAllChecked(false);
        setDeleteModalShow(false);
    };

    /**
     * Confirms deletion of the buffered items through the generic
     * delete-by-table endpoint, then refetches the assigned page.
     */
    const onDelete = () => {
        if (selectForDelete.length <= 0) return;

        dispatch(DeleteItemsApi({ tableName: dynamicConfig.model, body: selectForDelete }))
            .then(({ payload: { data: { success, code } } }) => {
                if (!success) {
                    errorMsg(code);
                    return;
                }
                setModified(true);
                hide();
            });
    };

    return {
        t,
        deletePermission,
        deleteModalShow,
        dynamicConfig,
        assignedEndPoint: GetBuildingUserAssigned,
        buildBody,
        handleDelete,
        handleCheckboxChange,
        handleDeleteAll,
        onDelete,
        hide,
    }
}
